import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext'; // Make sure this path is correct for your project

// Modal Component for Editing User
const EditUserModal = ({ user, isOpen, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState({});

  const isSelf = currentUser?.userId === user?._id;
  const isSuperAdmin = currentUser?.role === 'Super Admin';
  const selfRoleLocked = isSelf && !isSuperAdmin; // non–Super Admin editing self => role locked

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        username: user.username || '',
        email: user.email || '',
        role: user.role || 'Collector',
        password: '', // Initialize password field
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = { ...formData };

    // Prevent role changes when a non–Super Admin edits themselves (extra safety in UI)
    if (selfRoleLocked) {
      delete dataToSave.role;
    }

    // If password field is empty, don't send it in the update request
    if (!dataToSave.password) {
      delete dataToSave.password;
    }

    onSave(user._id, dataToSave);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg text-center leading-6 font-medium text-gray-900">Edit User</h3>
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="mt-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="mt-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="mt-1 px-3 py-2 text-gray-700 bg-gray-100 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500"
                placeholder="Leave blank to keep current password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={selfRoleLocked}
                className={`mt-1 px-3 py-2 text-gray-700 rounded-md w-full border border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                  selfRoleLocked ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-100'
                }`}
                title={selfRoleLocked ? 'You cannot change your own role.' : 'Select role'}
              >
                {/* Only Super Admins can assign the Super Admin role */}
                <option value="Super Admin" disabled={!isSuperAdmin}>Super Admin</option>
                <option value="Admin">Admin</option>
                <option value="Verifier">Verifier</option>
                <option value="Collector">Collector</option>
                <option value="Payment Redeemer">Payment Redeemer</option>
                <option value="Payment Poster">Payment Poster</option>
              </select>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// A component to display the list of all users in a table.
const UsersTable = ({ users, loading, onEdit, onDelete, currentUser }) => {
  if (loading) {
    return <div className="text-center p-8 text-gray-700">Loading users...</div>;
  }

  if (!users || users.length === 0) {
    return <div className="text-center p-8 text-gray-700">No users found.</div>;
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr>
            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Full Name</th>
            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Username</th>
            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isSuperAdminTarget = user.role === 'Super Admin';
            const isAdminActingOnSuperAdmin = currentUser?.role === 'Admin' && isSuperAdminTarget;
            const isSelfAction = currentUser?.userId === user._id;

            // Disable edit when: Admin tries to edit a Super Admin. Everyone can edit themselves.
            const editDisabled = isAdminActingOnSuperAdmin;

            // Disable delete when: Admin tries to delete Super Admin, OR user tries to delete self
            const deleteDisabled = isAdminActingOnSuperAdmin || isSelfAction;

            return (
              <tr key={user._id || user.id}>
                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p className="text-gray-900 whitespace-no-wrap">{user.fullName}</p>
                </td>
                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p className="text-gray-900 whitespace-no-wrap">{user.username}</p>
                </td>
                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <p className="text-gray-900 whitespace-no-wrap">{user.email}</p>
                </td>
                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <span className={`relative inline-block px-3 py-1 font-semibold leading-tight ${isSuperAdminTarget ? 'text-red-900' : 'text-purple-900'}`}>
                    <span aria-hidden className={`absolute inset-0 opacity-50 rounded-full ${isSuperAdminTarget ? 'bg-red-200' : 'bg-purple-200'}`}></span>
                    <span className="relative">{user.role}</span>
                  </span>
                </td>
                <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                  <button
                    onClick={() => onEdit(user)}
                    disabled={editDisabled}
                    className="text-indigo-600 hover:text-indigo-900 font-semibold mr-4 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      editDisabled
                        ? "Admins cannot edit Super Admins."
                        : "Edit user"
                    }
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(user)}
                    disabled={deleteDisabled}
                    className="text-red-600 hover:text-red-900 font-semibold transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      deleteDisabled
                        ? (isSelfAction ? "You cannot delete yourself." : "Admins cannot delete Super Admins.")
                        : "Delete user"
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(''); // ✅ success message state
  const { user: currentUser } = useAuth(); // Use the authenticated user from context

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const showSuccess = (msg) => {
    setSuccess(msg);
    // Auto-dismiss after 3 seconds
    window.clearTimeout(window.__successTimer);
    window.__successTimer = window.setTimeout(() => setSuccess(''), 3000);
  };

  const fetchAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch users.';
      setError(errorMessage);
      console.error("Failed to fetch users.", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
    return () => {
      // Cleanup the timer if component unmounts
      if (window.__successTimer) {
        window.clearTimeout(window.__successTimer);
      }
    };
  }, [fetchAllUsers]);

  const handleEdit = (user) => {
    // Allow users to edit their own info (role will be locked in the modal if not Super Admin)
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (userToDelete) => {
    if (currentUser?.userId === userToDelete._id) {
      setError('You cannot delete your own account.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the user "${userToDelete.fullName}"? This action cannot be undone.`)) {
      try {
        await api.delete(`/users/${userToDelete._id}`);
        setUsers(users.filter(user => user._id !== userToDelete._id));
        setError('');
        showSuccess(`User "${userToDelete.fullName}" deleted successfully.`);
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Failed to delete user.';
        setError(errorMessage);
        console.error("Failed to delete user.", err);
      }
    }
  };

  const handleSave = async (userId, updatedData) => {
    try {
      // Backend-enforced safeguard: if a non–Super Admin is saving their own profile, strip role from payload
      if (currentUser?.userId === userId && currentUser?.role !== 'Super Admin') {
        const { role, ...rest } = updatedData;
        updatedData = rest;
      }

      const response = await api.patch(`/users/${userId}`, updatedData);
      setUsers(users.map(user => (user._id === userId ? response.data : user)));
      setIsEditModalOpen(false);
      setEditingUser(null);
      setError('');
      showSuccess('User updated successfully.');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update user.';
      setError(errorMessage);
      console.error("Failed to update user.", err);
    }
  };

  return (
    <div className="mt-8">
      {error && (
        <p className="text-red-700 bg-red-100 border border-red-200 p-3 rounded-md mb-4">
          {error}
        </p>
      )}

      {success && (
        <p className="text-green-800 bg-green-100 border border-green-200 p-3 rounded-md mb-4">
          {success}
        </p>
      )}

      <UsersTable
        users={users}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        currentUser={currentUser}
      />

      <EditUserModal
        isOpen={isEditModalOpen}
        user={editingUser}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSave}
        currentUser={currentUser}
      />
    </div>
  );
};

export default ManageUsers;
